import { memo } from 'react';

import { Box, Divider, Icon, Text, TextButton } from '@zillow/constellation-10';

import { getConstellationIcon } from '../../../../../srcLegacy/shared/utils/iconUtils';
import { TRIGGER_LOCATIONS_MAP } from '../../../../../srcLegacy/shared/components/WizardModularListing/CostsAndFees/constants';

import { EVENT_IDS } from '../../../../analytics/events/singleFamilyCostsAndFees';
import { FeeListItem } from './FeeListItem';

import type {
  ExistingFeesDto,
  FeeCategoryDictionaryItemDto,
  GetSingleFamilyFeesDictionaryItemsDto,
  OtherFeesPseudoCategoryDto,
  SingleFamilyFeeDto,
} from '@zg-rentals/ts-rental-manager-api-beta-client';

type Props = {
  dictionaryItems: GetSingleFamilyFeesDictionaryItemsDto;
  existingFees: ExistingFeesDto;
  handleAddFeeClick: (fixedCategory?: string) => void;
  handleEditFeeClick: (fee: SingleFamilyFeeDto) => void;
  displayedLocation: string;
};

const getFeeMainCategoryDisplay = (
  feeCategoryKey: string,
  dictionaryItems: Record<string, FeeCategoryDictionaryItemDto>,
  handleAddFeeClick: (fixedCategory?: string) => void,
) => {
  const { displayName, feeCategoryIconName } = dictionaryItems[feeCategoryKey];
  const IconComponent = getConstellationIcon({ iconName: feeCategoryIconName });
  return (
    <Box css={{ paddingBlock: '400', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
      <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
        {IconComponent && <Icon size="md" render={<IconComponent />} />}
        <Text textStyle="body-bold">{displayName}</Text>
      </Box>
      <TextButton
        onClick={() => handleAddFeeClick(feeCategoryKey)}
        data-testid={`CostsAndFees-Add${displayName}FeeButton`}
        data-eventid={EVENT_IDS.CLICK_ADD_SF_FEE}
      >
        Add
      </TextButton>
    </Box>
  );
};

const getFeeOtherCategoryDisplay = (
  dictionaryItems: OtherFeesPseudoCategoryDto,
  handleAddFeeClick: (fixedCategory?: string) => void,
) => {
  const { displayName, feeCategoryIconName } = dictionaryItems;
  const IconComponent = getConstellationIcon({ iconName: feeCategoryIconName });
  return (
    <Box css={{ paddingBlock: '400', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
      <Box css={{ display: 'flex', flexDirection: 'row', gap: '400' }}>
        {IconComponent && <Icon size="md" render={<IconComponent />} />}
        <Text textStyle="body-bold">{displayName}</Text>
      </Box>
      <TextButton onClick={() => handleAddFeeClick()} data-eventid={EVENT_IDS.CLICK_ADD_SF_FEE}>
        Add
      </TextButton>
    </Box>
  );
};

export const FeesByCategory = memo(
  ({ dictionaryItems, existingFees, handleAddFeeClick, handleEditFeeClick, displayedLocation }: Props) => {
    return (
      <div data-testid="SingleFamilyFeesByCategory">
        {Object.keys(dictionaryItems.mainCategories).map((categoryKey) => (
          <Box key={categoryKey}>
            {getFeeMainCategoryDisplay(categoryKey, dictionaryItems.mainCategories, handleAddFeeClick)}

            <Divider />
            {existingFees.mainCategories[categoryKey as keyof typeof existingFees.mainCategories]?.map(
              (fee: SingleFamilyFeeDto) => (
                <Box
                  key={fee.costAndFeeId}
                  css={{
                    marginLeft: displayedLocation === TRIGGER_LOCATIONS_MAP.posting_path_review ? '800' : '0',
                  }}
                >
                  <FeeListItem
                    fee={fee}
                    dictionaryItems={dictionaryItems}
                    handleEditFeeClick={handleEditFeeClick}
                    displayedLocation={displayedLocation}
                  />
                </Box>
              ),
            )}
          </Box>
        ))}
        <Box key="otherCategories">
          {getFeeOtherCategoryDisplay(dictionaryItems.otherCategories, handleAddFeeClick)}

          <Divider />
          {existingFees.otherCategories.map((fee: SingleFamilyFeeDto) => (
            <Box
              key={fee.costAndFeeId}
              css={{
                marginLeft: displayedLocation === TRIGGER_LOCATIONS_MAP.posting_path_review ? '800' : '0',
              }}
            >
              <FeeListItem
                fee={fee}
                dictionaryItems={dictionaryItems}
                handleEditFeeClick={handleEditFeeClick}
                displayedLocation={displayedLocation}
              />
            </Box>
          ))}
        </Box>
      </div>
    );
  },
);

FeesByCategory.displayName = 'FeesByCategory';
